import { NavLink } from "react-router-dom";

import { cn } from "@/lib/utils";

// Story 8.7 (gh-246) — calibration-local secondary nav (Screen 15/16 "Overview /
// Case Studies"). Rendered IN-CONTENT inside the AppShell <main>, not as a second
// sidebar: the mockup's standalone calibration shell reconciles to a horizontal
// tab-rail here (the eval-flow step-rail precedent, CLAUDE.md) so it composes with
// the global app nav instead of fighting it. NavLink sets aria-current="page" on the
// active route automatically (a11y), and `end` pins the Overview match so it doesn't
// stay active on the /calibration/cases child paths.
const ITEMS = [
  { to: "/calibration/overview", label: "Overview", end: true },
  { to: "/calibration/cases", label: "Case Studies", end: false },
] as const;

const TAB_BASE =
  "-mb-px min-h-[44px] px-4 py-2 text-sm font-semibold transition-colors " +
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--focus-ring)] focus-visible:ring-offset-2 focus-visible:ring-offset-[color:var(--bg-primary)]";

/**
 * The id that ties the visible reason to the inert tab (DW-680). Visible text
 * alone puts the sentence in the document but not in the accessible description
 * of the control it explains, so a screen-reader user who lands on the disabled
 * tab is told it is unavailable and not why. Same shape as
 * `EditMemberDialog.tsx:73-98`.
 */
const HELD_REASON_ID = "calibration-subnav-held-reason";

/**
 * Story 20.2 (FR86) — `heldReason` makes the "Case Studies" tab INERT.
 *
 * A PROP rather than a `useCalibrationStatus` call inside this component
 * (boundary 10): three calibration screens mount this nav and it takes no props
 * today, so a self-read would add a query observer to all three and change two
 * screens this story does not otherwise touch — 20.1 boundary 8 already records
 * what handing a query client to an existing bare harness costs.
 *
 * The inert tab is a plain `<span>`, not a `<NavLink>` that swallows its click:
 * P0-CAL-09's shape is that a locked affordance is not a navigation element at
 * all, so there is nothing to middle-click, copy, or reach by keyboard. It keeps
 * `aria-disabled` for a reader who meets it anyway.
 *
 * The reason renders as VISIBLE TEXT under the rail, never as a native `title`
 * (D9): the iPad PWA is the primary clinical surface and `title` is mouse-only
 * there (the standing gh-555/gh-568 finding). The mockup uses `title=`; that is
 * recorded as mockup hygiene in the W3 reconciliation, not copied.
 *
 * It is also LINKED to the inert tab with `aria-describedby` (DW-680). Visible
 * text puts the sentence in the document; the linkage puts it in the accessible
 * description of the control it explains, which is what a screen-reader user
 * landing on the disabled tab actually reaches.
 *
 * Absent `heldReason`, this renders exactly what Story 8.7 shipped — the two
 * NavLinks, same classes — so the three existing mount sites are unchanged.
 */
export function CalibrationSubNav({ heldReason }: { heldReason?: string } = {}) {
  return (
    <div className="flex flex-col gap-1">
      <nav
        aria-label="Calibration"
        className="flex gap-1 border-b border-[color:var(--border-primary)]"
      >
        {ITEMS.map((item) =>
          heldReason && item.to === "/calibration/cases" ? (
            <span
              key={item.to}
              aria-disabled="true"
              aria-describedby={HELD_REASON_ID}
              className={cn(
                TAB_BASE,
                "cursor-not-allowed text-[color:var(--text-quaternary)]",
              )}
            >
              {item.label}
            </span>
          ) : (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                cn(
                  TAB_BASE,
                  isActive
                    ? "border-b-2 border-[color:var(--border-brand-alt)] text-[color:var(--text-brand-secondary)]"
                    : "text-[color:var(--text-tertiary)] hover:text-[color:var(--text-primary)]",
                )
              }
            >
              {item.label}
            </NavLink>
          ),
        )}
      </nav>
      {heldReason ? (
        <p
          id={HELD_REASON_ID}
          className="text-xs text-[color:var(--text-tertiary)]"
        >
          {heldReason}
        </p>
      ) : null}
    </div>
  );
}
